import {
  CapTableTransaction,
  BusinessEntity,
  CorporationType,
  Country,
  Currency,
  Director,
  Stakeholder,
  StakeholderRelationshipType,
  StakeholderType,
  Stakeholding,
  StockClass,
  StockClassType,
} from '@commenda/prisma';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  InvoicePaymentsResponse,
  InvoicesResponse,
} from '@commenda/types/src/root-fi/types';
import { formatDirectorName } from './format-director-name';

export type FileBasicWithData<T> = FileBasic & {
  data?: T;
};

interface SerializedBankTransaction {
  amount: number;
  category: string[];
  date: string;
  id: string;
  image: string;
  name: string;
}

type SerializedStakeholder = {
  name: string;
  email: string;
  type: StakeholderType;
  relationshipType: StakeholderRelationshipType;
  id: string;
  numberOfShares: number;
  amount: number;
  nationality: Country;
  corporationId?: number;
};

const serializeStakeholder = (
  stakeholder: Stakeholder &
    Partial<{
      corporation: BusinessEntity;
      director: Director;
      stakeholdings: Stakeholding[];
      relationshipType: StakeholderRelationshipType;
      transactionAdditionalInformation: {
        numberOfShares: number;
        price: number;
      };
    }>
): SerializedStakeholder => {
  if (stakeholder?.directorId)
    return {
      name: formatDirectorName(stakeholder.director),
      email: stakeholder.director.email,
      type: stakeholder.type,
      relationshipType: stakeholder.relationshipType,
      id: stakeholder.id,
      numberOfShares:
        stakeholder?.transactionAdditionalInformation?.numberOfShares,
      amount: stakeholder?.transactionAdditionalInformation?.price,
      nationality: stakeholder.nationality || null,
    };

  if (stakeholder?.corporationId)
    return {
      name: stakeholder.corporation.companyLegalName,
      email: '',
      type: stakeholder.type,
      relationshipType: stakeholder.relationshipType,
      id: stakeholder.id,
      numberOfShares:
        stakeholder?.transactionAdditionalInformation?.numberOfShares,
      amount: stakeholder?.transactionAdditionalInformation?.price,
      nationality: stakeholder.corporation.incorporationCountry || null,
      corporationId: stakeholder.corporationId,
    };

  if (stakeholder?.additionalInformation)
    return {
      name: JSON.parse(JSON.stringify(stakeholder.additionalInformation))
        .primaryContact.name,
      email: JSON.parse(JSON.stringify(stakeholder.additionalInformation))
        .primaryContact.emails?.[0],
      type: stakeholder.type,
      relationshipType: stakeholder.relationshipType,
      id: stakeholder.id,
      numberOfShares:
        stakeholder?.transactionAdditionalInformation?.numberOfShares || 0,
      amount: stakeholder?.transactionAdditionalInformation?.price || 0,
      nationality: stakeholder?.nationality || null,
    };

  return null;
};

type SerializedTransaction = {
  id: string;
  date: string;
  noOfShares: number;
  sharePrice: number;
  stakeholder: string;
  stockClass: string;
  stockClassType: StockClassType;
};

const serializeTransaction = (
  transaction: CapTableTransaction & {
    stakeholding: Stakeholding & { stakeholder: Stakeholder };
    stockClass: StockClass;
  }
): SerializedTransaction => {
  return {
    id: transaction.id,
    date: transaction.date,
    noOfShares: JSON.parse(JSON.stringify(transaction.additionalInformation))
      ?.quantity,
    sharePrice: JSON.parse(JSON.stringify(transaction.additionalInformation))
      ?.sharePrice?.amount,
    stakeholder: serializeStakeholder(transaction?.stakeholding?.stakeholder)
      ?.name,
    stockClass: transaction?.stockClass?.name,
    stockClassType: transaction?.stockClass?.type,
  };
};

type SerializedStockClass = {
  amountRaised: number;
  authorizedShares: number;
  authorizedSharesType: string;
  id: string;
  name: string;
  outstandingShares: number;
  type: string;
  additionalInformation: {
    pricePerShare?: {
      amount: number;
      currency: Currency;
    };
  };
};

type ItemData = {
  name: string;
  id: number;
  quantity: number;
  rate: number;
  tax_amount: number;
  amount: number;
  discount: number;
  sub_total: number;
};

type SerializedInvoiceData = {
  document_number: string;
  total_amount: number;
  sub_total: number;
  tax_amount: number;
  discount: number;
  date: string;
  due_date: string;
  name?: string;
  items?: ItemData[];
};

const serializeRootFiInvoicesData = (
  invoicesResponse: InvoicesResponse
): SerializedInvoiceData[] => {
  const invoices = invoicesResponse?.invoices;

  return invoices?.data?.map((invoice) => ({
    document_number: invoice.document_number,
    total_amount: invoice.total_amount,
    sub_total: invoice.sub_total,
    tax_amount: invoice.tax_amount,
    discount: invoice.total_discount,
    date: invoice.posted_date,
    due_date: invoice.due_date,
    name: invoice.contact?.data?.name,
    items: invoice.line_items?.data?.map((item, idx) => ({
      name: item.description,
      id: idx + 1,
      quantity: item.quantity,
      rate: item.unit_amount,
      tax_amount: item.tax_amount,
      amount: item.total_amount,
      discount: item.total_discount,
      sub_total: item.sub_total,
    })),
  }));
};

type SerializedInvoicePaymentData = {
  address: {
    city: string | null;
    state: string | null;
    pinCode: string | null;
    country: string | null;
  };
  name: string;
  currency_code: string;
  date: string;
  tax_amount: number;
  sub_total: number;
  total_amount: number;
  currency: string | undefined;
};

const serializeRootFiInvoicePaymentsData = (
  invoicesResponse: InvoicePaymentsResponse
): { data: SerializedInvoicePaymentData[] } => {
  const invoicePayments = invoicesResponse?.invoice_payments;

  return {
    data: invoicePayments?.map((invoice_payment) => {
      const billingAddress =
        invoice_payment?.contact?.data?.addresses?.data?.find(
          (address) => address.type === 'BILLING'
        );
      const shippingAddress =
        invoice_payment?.contact?.data?.addresses?.data?.find(
          (address) => address.type === 'SHIPPING'
        );

      return {
        address: {
          city: billingAddress ? billingAddress?.city : shippingAddress?.city,
          state: billingAddress
            ? billingAddress?.state
            : shippingAddress?.state,
          pinCode: billingAddress
            ? billingAddress?.pincode
            : shippingAddress?.pincode,
          country: billingAddress
            ? billingAddress?.country
            : shippingAddress?.country,
        },
        name: invoice_payment.contact.data.name,
        currency_code: invoice_payment.invoice?.data?.currency?.data?.code,
        date: invoice_payment?.payment_date,
        tax_amount: invoice_payment?.invoice?.data?.tax_amount,
        sub_total: invoice_payment?.invoice?.data?.sub_total,
        total_amount: invoice_payment?.invoice?.data?.total_amount,
        currency: invoice_payment?.invoice?.data?.currency?.data?.code,
      };
    }),
  };
};

type SAFEFileRequestDataType = {
  id: string;
  dateOfSafe: string;
  investorName: string;
  investorEmail: string;
  investmentAmount: number;
  postMoneyValuation: number;
  corporationType: CorporationType;
  directorDesignation: string;
  directorName: string;
  hasDirectorSigned: boolean;
  hasInvestorSigned: boolean;
  stateOfIncorporation: string;
};

type SerializedOutstandingSAFEs = {
  id: string;
  date: string;
  investorName: string;
  investorEmail: string;
  investmentAmount: number;
  postMoneyValuation: number;
  signeeDirector: string;
  hasInvestorSigned: boolean;
  file: FileBasicWithData<SAFEFileRequestDataType>;
};

const serializeOutstandingSAFEs = (
  outstandingSafeFiles: FileBasicWithData<SAFEFileRequestDataType>[]
): SerializedOutstandingSAFEs[] => {
  return outstandingSafeFiles?.map((file) => {
    const docData = file?.data;

    return {
      id: docData?.id,
      date: docData?.dateOfSafe,
      investorName: docData?.investorName,
      investorEmail: docData?.investorEmail,
      investmentAmount: docData?.investmentAmount,
      postMoneyValuation: docData?.postMoneyValuation,
      signeeDirector: docData?.directorName,
      hasInvestorSigned: docData?.hasInvestorSigned,
      file,
    };
  });
};

export {
  serializeOutstandingSAFEs,
  serializeRootFiInvoicePaymentsData,
  serializeRootFiInvoicesData,
  serializeStakeholder,
  serializeTransaction,
};
export type {
  SAFEFileRequestDataType,
  SerializedInvoiceData,
  SerializedInvoicePaymentData,
  SerializedOutstandingSAFEs,
  SerializedStakeholder,
  SerializedStockClass,
  SerializedTransaction,
};
